import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { PiXLight, PiCloudArrowDownLight, PiArrowsOutSimpleLight, PiPaperPlaneTiltLight, PiImageLight, PiFilePdfLight, PiFileDocLight, PiFileXlsLight, PiFileTextLight, PiFileZipLight, PiFileVideoLight, PiFileAudioLight, PiPaperclipLight } from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import apis from '../../../utils/apis';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { useAppDispatch } from '../../../store';
import { createTicketComment, fetchTicketComments } from '../../../store/actions/HelpAction';

interface TicketDetailsContentProps {
  ticket: any;
}

const TicketDetailsContent: React.FC<TicketDetailsContentProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageCache, setImageCache] = useState<{ [key: string]: string }>({});
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const formattedSupportId = String(ticket.id || ticket.ticket_id).padStart(6, '0');

  // Fetch comments on component mount
  useEffect(() => {
    const loadComments = async () => {
      setIsLoadingComments(true);
      const result = await dispatch(fetchTicketComments(ticket.id) as any);
      if (result?.comments) {
        setComments(result.comments);
      }
      setIsLoadingComments(false);
    };
    loadComments();
  }, [ticket.id, dispatch]);

  const getStatusColor = (status: string): 'gray' | 'yellow' | 'blue' | 'green' | 'orange' | 'red' => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'blue';
      case 'in_progress':
        return 'blue';
      case 'closed':
        return 'green';
      case 'pending_user':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getPriorityColor = (priority: string): 'gray' | 'green' | 'yellow' | 'orange' | 'red' => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (filename: string, contentType?: string): boolean => {
    const ext = getFileExtension(filename).toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'];
    if (imageExtensions.includes(ext)) return true;
    return contentType?.startsWith('image/') || false;
  };

  const getFileIcon = (filename: string, contentType?: string): JSX.Element => {
    const ext = getFileExtension(filename).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(ext)) return <PiImageLight size={20} />;
    if (ext === 'pdf') return <PiFilePdfLight size={20} />;
    if (['doc', 'docx', 'docm', 'dot', 'dotx'].includes(ext)) return <PiFileDocLight size={20} />;
    if (['xls', 'xlsx', 'xlsm', 'csv'].includes(ext)) return <PiFileXlsLight size={20} />;
    if (['txt', 'md', 'json', 'xml', 'yaml', 'yml'].includes(ext)) return <PiFileTextLight size={20} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <PiFileZipLight size={20} />;
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return <PiFileVideoLight size={20} />;
    if (['mp3', 'wav', 'aac', 'flac', 'ogg'].includes(ext)) return <PiFileAudioLight size={20} />;
    return <PiPaperclipLight size={20} />;
  };

  const getImageDataUrl = async (attachment: any): Promise<string> => {
    if (imageCache[attachment.attachment_id]) {
      return imageCache[attachment.attachment_id];
    }

    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });

      const dataUrl = URL.createObjectURL(response.data);
      setImageCache(prev => ({ ...prev, [attachment.attachment_id]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.error('Failed to load image:', error);
      return '';
    }
  };

  useEffect(() => {
    if (ticket.attachments && ticket.attachments.length > 0) {
      ticket.attachments.forEach((attachment: any) => {
        if (isImageFile(attachment.filename, attachment.content_type) && !imageCache[attachment.attachment_id]) {
          getImageDataUrl(attachment);
        }
      });
    }
  }, [ticket.id, ticket.attachments]);

  const handleImageClick = async (attachment: any) => {
    const dataUrl = await getImageDataUrl(attachment);
    setSelectedImage(dataUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleDownload = async (attachment: any) => {
    try {
      const downloadUrl = `/support/tickets/${ticket.id}/attachments/${attachment.attachment_id}/download`;
      const response = await apis({
        method: 'GET',
        url: downloadUrl,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response = await apis({
        method: 'POST',
        url: `/support/tickets/${ticket.id}/attachments/upload`,
        data: formData,
      });

      if (response.data && response.data.success) {
        // Refresh ticket data to get new attachments
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handlePostComment = async () => {
    if (!commentText.trim()) return;

    setIsPostingComment(true);
    const result = await dispatch(createTicketComment(ticket.id, commentText) as any);
    
    if (result?.success) {
      setCommentText('');
      // Refresh comments
      const updatedComments = await dispatch(fetchTicketComments(ticket.id) as any);
      if (updatedComments?.comments) {
        setComments(updatedComments.comments);
      }
    }
    setIsPostingComment(false);
  };

  return (
    <div className="w-full h-[calc(100vh-110px)] flex gap-4 p-4">
      {/* LEFT COLUMN - Details & Attachments (60%) - Static, No Scroll */}
      <div data-tour-id="ticket-details-left" className="w-3/5 flex flex-col gap-6 overflow-hidden">
        {/* Details Card - Static */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex-shrink-0">
          {/* Ticket ID and Title */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">#{formattedSupportId}</h1>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{ticket.title}</h2>
          </div>

          {/* Status and Priority - Same Row */}
          <div data-tour-id="ticket-status-priority" className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('Status')}</label>
              <Badge color={getStatusColor(ticket.status)} className="capitalize text-xs">
                {ticket.status || 'open'}
              </Badge>
            </div>

            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('Priority')}</label>
              <Badge color={getPriorityColor(ticket.priority)} className="capitalize text-xs">
                {ticket.priority || 'medium'}
              </Badge>
            </div>
          </div>

          {/* Created and Updated - Same Row with Time */}
          <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">{t('Created')}</label>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {ticket.created_at && format(new Date(ticket.created_at), 'MMM d, yyyy HH:mm')}
              </p>
            </div>

            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-medium">{t('Updated')}</label>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {ticket.updated_at && format(new Date(ticket.updated_at), 'MMM d, yyyy HH:mm')}
              </p>
            </div>
          </div>

          {/* Description */}
          <div data-tour-id="ticket-description" className="mb-4">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('Description')}</label>
            <div className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap ${
              (ticket.description?.length || 0) > 900
                ? 'custom-scrollbar max-h-[200px] overflow-y-auto px-3'
                : ''
            }`}>
              {ticket.description}
            </div>
          </div>

          {/* Resolution */}
          {ticket.resolution && (
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('Resolution')}</label>
              <p className="text-sm text-gray-700 dark:text-gray-300">{ticket.resolution}</p>
            </div>
          )}
        </div>

        {/* Attachments Card - Static */}
        <div data-tour-id="ticket-attachments" className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex-1 flex flex-col overflow-hidden max-h-[280px]">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('Attachments')} {ticket.attachments && ticket.attachments.length > 0 && `(${ticket.attachments.length})`}
            </h3>
          </div>

          {!ticket.attachments || ticket.attachments.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('No attachments yet')}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{t('Upload files to get started')}</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-hidden">
            {ticket.attachments.map((attachment: any) => {
                const isImage = isImageFile(attachment.filename, attachment.content_type);
                const fileIcon = getFileIcon(attachment.filename, attachment.content_type);
                const cachedImageUrl = imageCache[attachment.attachment_id];

                if (isImage) {
                  return (
                    <div key={attachment.attachment_id} className="relative group cursor-pointer rounded-lg overflow-hidden">
                      {cachedImageUrl ? (
                        <>
                          <img
                            src={cachedImageUrl}
                            alt={attachment.filename}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                            onClick={() => handleImageClick(attachment)}
                            title={t('Click to view full size')}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(attachment);
                                }}
                                className="text-white bg-black bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
                                title={t('View')}
                              >
                                <PiArrowsOutSimpleLight size={20} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(attachment);
                                }}
                                className="text-white bg-black bg-opacity-70 p-2 rounded-full hover:bg-opacity-90 transition-all"
                                title={t('Download')}
                              >
                                <PiCloudArrowDownLight size={20} />
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 flex items-center justify-center cursor-pointer">
                          <div className="text-center">
                            <p className="text-xs text-gray-600 dark:text-gray-400">{t('Loading image...')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } else {
                  return (
                    <div key={attachment.attachment_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 text-gray-700 dark:text-gray-300">{fileIcon}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-900 dark:text-white truncate">{attachment.filename}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(attachment.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(attachment)}
                        className="ml-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded hover:bg-white dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                        title={t('Download')}
                      >
                        <PiCloudArrowDownLight size={18} />
                      </button>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT COLUMN - Comments (40%) - Scrollable */}
      <div data-tour-id="ticket-comments" className="w-2/5 flex flex-col h-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col h-full">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Comments ({comments.length})</h3>

          {/* Comment Input */}
          <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={t('Add a comment...') || 'Add a comment...'}
              className="w-full h-20 p-2 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-none"
            />
            <Button
              onClick={handlePostComment}
              disabled={!commentText.trim()}
              isLoading={isPostingComment}
              variant="primary"
              size="small"
              className="mt-2 w-full"
              icon={<PiPaperPlaneTiltLight size={14} />}
            >
              {t('Post')}
            </Button>
          </div>

          {/* Comments List - Only This Scrolls */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {isLoadingComments ? (
              <div className="text-center py-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('Loading...')}</p>
              </div>
            ) : comments.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">{t('No comments yet')}</p>
            ) : (
              comments.map((comment: any, idx: number) => (
                <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100">You</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {comment.created_at && format(new Date(comment.created_at), 'MMM d, HH:mm')}
                    </p>
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comment.content}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 animate-fade-in p-4">
          <div className="relative max-w-5xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-2xl">
            <img src={selectedImage} alt="Full size" className="max-w-full max-h-[85vh] object-contain" />
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <Tooltip title={t('Close')}>
                <button
                  onClick={handleCloseModal}
                  className="text-white bg-black bg-opacity-60 hover:bg-opacity-80 p-2 rounded-full transition-all duration-200 flex items-center justify-center"
                >
                  <PiXLight size={24} />
                </button>
              </Tooltip>
              <Tooltip title={t('Download')}>
                <a
                  href={selectedImage}
                  download
                  className="text-white bg-black bg-opacity-60 hover:bg-opacity-80 p-2 rounded-full transition-all duration-200 flex items-center justify-center"
                >
                  <PiCloudArrowDownLight size={24} />
                </a>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailsContent;
